import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { LogOut, Upload, Trash2, Pencil, ArrowLeft, ShieldCheck, Loader2, Image as ImageIcon, X as XIcon } from "lucide-react";
import { api } from "@/lib/api";
import { SHOPS, STORE } from "@/lib/config";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const TOKEN_KEY = "moby_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  return token ? (
    <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />
  ) : (
    <LoginScreen onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { token } = await api.login(username, password);
      onLogin(token);
      toast.success("Dobrodošli, admine!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Greška prilikom prijave");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-neutral-50 p-4" data-testid="admin-login">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-neutral-500 hover:text-[#D32F2F] mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Nazad na stranicu
        </Link>
        <Card className="p-8 rounded-2xl border-neutral-200">
          <div className="w-14 h-14 grid place-items-center bg-[#D32F2F] rounded-2xl mb-4">
            <ShieldCheck className="text-white" />
          </div>
          <h1
            className="text-3xl font-extrabold text-neutral-900 mb-1"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            Admin prijava
          </h1>
          <p className="text-neutral-500 text-sm mb-6">
            {STORE.name} {STORE.city} — upravljanje proizvodima
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="u">Korisničko ime</Label>
              <Input
                id="u"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p">Lozinka</Label>
              <Input
                id="p"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="w-full bg-[#D32F2F] hover:bg-[#B71C1C] h-11 rounded-full font-semibold"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Prijavi se"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const { resolved, refresh: refreshSettings } = useSettings();
  const [shop, setShop] = useState("moby_shop");
  const [subcategory, setSubcategory] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [products, setProducts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [bagMessage, setBagMessage] = useState("");
  const [bookshopTitle, setBookshopTitle] = useState("");
  const [savingMsg, setSavingMsg] = useState(false);

  const loadProducts = async () => {
    setLoadingList(true);
    try {
      const data = await api.listProducts();
      setProducts(data);
    } finally {
      setLoadingList(false);
    }
  };

  const loadSettings = async () => {
    try {
      const s = await api.getSettings();
      setBagMessage(s.bag_message || "");
      setBookshopTitle(s.bookshop_title || "");
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadProducts();
    loadSettings();
  }, []);

  const saveMessage = async () => {
    if (!bagMessage.trim() || !bookshopTitle.trim()) {
      toast.error("Polja ne mogu biti prazna");
      return;
    }
    setSavingMsg(true);
    try {
      await api.updateSettings(token, {
        bag_message: bagMessage.trim(),
        bookshop_title: bookshopTitle.trim(),
      });
      toast.success("Tekstovi knjižare spremljeni!");
      refreshSettings();
    } catch {
      toast.error("Greška prilikom spremanja");
    } finally {
      setSavingMsg(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Slika je veća od 2MB. Molim odaberi manju.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editingId && !image) { toast.error("Odaberite sliku proizvoda"); return; }
    setBusy(true);
    try {
      const payload = {
        shop,
        subcategory: subcategory.trim(),
        name: name.trim(),
        description: description.trim(),
        price: parseFloat(price),
      };
      // Only include image if user picked a new one (empty string means keep existing)
      if (image) payload.image = image;

      if (editingId) {
        await api.updateProduct(token, editingId, payload);
        toast.success("Proizvod ažuriran!");
      } else {
        payload.image = image;
        await api.createProduct(token, payload);
        toast.success("Proizvod dodan!");
      }
      resetForm();
      loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Greška prilikom spremanja");
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setName(""); setDescription(""); setPrice(""); setImage(""); setSubcategory("");
    setShop("moby_shop"); setEditingId(null);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setShop(p.shop);
    setSubcategory(p.subcategory);
    setName(p.name);
    setDescription(p.description);
    setPrice(String(p.price));
    setImage(""); // Empty means "keep existing image"
    // Scroll form into view
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!confirm("Obrisati ovaj proizvod?")) return;
    try {
      await api.deleteProduct(token, id);
      toast.success("Proizvod obrisan");
      loadProducts();
    } catch {
      toast.error("Greška prilikom brisanja");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Top bar */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 grid place-items-center bg-[#D32F2F] rounded-xl">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-bold text-neutral-900 leading-tight">Admin Panel</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#D32F2F]">
                {STORE.name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="px-4 py-2 text-sm text-neutral-600 hover:text-[#D32F2F] rounded-full"
            >
              ← Stranica
            </Link>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={onLogout}
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" /> Odjava
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* IMAGES MANAGEMENT */}
        <ImagesManager token={token} resolved={resolved} refresh={refreshSettings} />

        {/* BAG MESSAGE EDITOR */}
        <Card className="p-6 rounded-2xl border-neutral-200 bg-gradient-to-br from-red-50/40 to-white">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2
                className="text-2xl font-extrabold text-neutral-900 mb-1"
                style={{ fontFamily: "Cabinet Grotesk" }}
              >
                🎒 Tekstovi sekcije &bdquo;Knjižara&ldquo;
              </h2>
              <p className="text-sm text-neutral-500">
                Naslov sekcije i tekst koji izgovara maskota torba. Promjena se odmah vidi.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Naslov sekcije</Label>
              <Input
                data-testid="bookshop-title-input"
                value={bookshopTitle}
                onChange={(e) => setBookshopTitle(e.target.value)}
                placeholder="npr. Vrijeme je za novu školsku godinu!"
                className="bg-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Poruka torbe (u oblačiću)</Label>
              <Textarea
                data-testid="bag-message-input"
                value={bagMessage}
                onChange={(e) => setBagMessage(e.target.value)}
                rows={3}
                placeholder="npr. Hej! Pripremimo se za novu školsku godinu!"
                className="bg-white"
              />
            </div>
          </div>

          <div className="flex items-center justify-end mt-4">
            <Button
              onClick={saveMessage}
              disabled={savingMsg}
              data-testid="bag-message-save"
              className="bg-[#D32F2F] hover:bg-[#B71C1C] rounded-full px-6"
            >
              {savingMsg ? <Loader2 className="w-4 h-4 animate-spin" /> : "Spremi tekstove"}
            </Button>
          </div>
        </Card>

        <div className="grid lg:grid-cols-5 gap-8">
        {/* FORM */}
        <Card className={`lg:col-span-2 p-6 rounded-2xl border-neutral-200 h-fit sticky top-20 ${editingId ? "ring-2 ring-[#D32F2F] shadow-lg" : ""}`}>
          <div className="flex items-start justify-between gap-3 mb-1">
            <h2
              className="text-2xl font-extrabold text-neutral-900"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              {editingId ? "✏️ Uredi proizvod" : "Dodaj proizvod"}
            </h2>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                data-testid="edit-cancel"
                className="text-xs text-neutral-500 hover:text-[#D32F2F] font-medium"
              >
                ✕ Otkaži
              </button>
            )}
          </div>
          <p className="text-sm text-neutral-500 mb-6">
            {editingId
              ? "Ažuriraj podatke. Sliku ne moraš ponovno uploadati ako je zadržavaš."
              : "Odaberite trgovinu, podkategoriju i popunite podatke."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Trgovina</Label>
              <Select value={shop} onValueChange={setShop}>
                <SelectTrigger data-testid="form-shop">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHOPS.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Podkategorija</Label>
              <Input
                data-testid="form-subcategory"
                placeholder="npr. Mobiteli, Pernice, Torbe…"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label>Naziv proizvoda</Label>
              <Input
                data-testid="form-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label>Opis proizvoda</Label>
              <Textarea
                data-testid="form-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                placeholder="Detaljan opis proizvoda — može više redova, karakteristike, specifikacije…"
                required
              />
              <p className="text-xs text-neutral-400">
                Duži opis? Na stranici će biti skraćen sa &bdquo;više&ldquo; dugmetom.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Cijena (KM)</Label>
              <Input
                data-testid="form-price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label>Slika proizvoda {editingId && <span className="text-xs text-neutral-400 font-normal">(opcionalno — zadrži postojeću)</span>}</Label>
              <label className="flex items-center gap-3 border-2 border-dashed border-neutral-300 hover:border-[#D32F2F] rounded-xl px-4 py-6 cursor-pointer transition-colors">
                <Upload className="w-5 h-5 text-neutral-400" />
                <span className="text-sm text-neutral-500">
                  {image
                    ? "Nova slika učitana ✓ (klikni za promjenu)"
                    : editingId
                    ? "Odaberi novu sliku ili zadrži postojeću"
                    : "Odaberite sliku (max 2MB)"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="form-image"
                  onChange={onFile}
                />
              </label>
              {image && (
                <img src={image} alt="preview" className="mt-2 w-32 h-32 object-cover rounded-lg" />
              )}
            </div>

            <Button
              type="submit"
              data-testid="form-submit"
              disabled={busy}
              className="w-full bg-[#D32F2F] hover:bg-[#B71C1C] h-11 rounded-full font-semibold"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : editingId ? (
                "Spremi izmjene"
              ) : (
                "Dodaj proizvod"
              )}
            </Button>
          </form>
        </Card>

        {/* LIST */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-2xl font-extrabold text-neutral-900"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Postojeći proizvodi ({products.length})
            </h2>
          </div>
          {loadingList ? (
            <div className="py-20 grid place-items-center">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : products.length === 0 ? (
            <Card className="p-10 text-center text-neutral-500 rounded-2xl">
              Još nema dodanih proizvoda.
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {products.map((p) => (
                <Card
                  key={p.id}
                  data-testid={`admin-product-${p.id}`}
                  className={`p-3 rounded-2xl flex gap-3 border-neutral-200 ${
                    editingId === p.id ? "ring-2 ring-[#D32F2F] bg-red-50/40" : ""
                  }`}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-20 h-20 object-cover rounded-lg bg-neutral-100"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#D32F2F] font-bold">
                      {SHOPS.find((s) => s.key === p.shop)?.label} · {p.subcategory}
                    </div>
                    <div className="font-bold text-neutral-900 line-clamp-1">{p.name}</div>
                    <div className="text-sm text-neutral-500 line-clamp-1">{p.description}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-bold text-[#D32F2F]">{p.price.toFixed(2)} KM</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEdit(p)}
                          data-testid={`admin-edit-${p.id}`}
                          className="text-neutral-400 hover:text-[#D32F2F] p-1.5 rounded-full hover:bg-red-50 transition-colors"
                          aria-label="Uredi"
                          title="Uredi proizvod"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          data-testid={`admin-delete-${p.id}`}
                          className="text-neutral-400 hover:text-[#D32F2F] p-1.5 rounded-full hover:bg-red-50 transition-colors"
                          aria-label="Obriši"
                          title="Obriši proizvod"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

// ============ Image management ============

function readFileAsDataURL(file, maxMB = 3) {
  return new Promise((resolve, reject) => {
    if (file.size > maxMB * 1024 * 1024) {
      reject(new Error(`Slika je veća od ${maxMB}MB`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImagesManager({ token, resolved, refresh }) {
  const [busy, setBusy] = useState(false);

  const save = async (payload, successMsg) => {
    setBusy(true);
    try {
      await api.updateSettings(token, payload);
      toast.success(successMsg || "Spremljeno!");
      refresh();
    } catch {
      toast.error("Greška prilikom spremanja");
    } finally {
      setBusy(false);
    }
  };

  const handleSingle = async (e, field, label) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await readFileAsDataURL(file);
      await save({ [field]: data }, `${label} spremljena!`);
    } catch (err) {
      toast.error(err.message || "Greška");
    }
  };

  const handleList = async (e, field, idx, list, label) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await readFileAsDataURL(file);
      const next = [...list];
      next[idx] = data;
      await save({ [field]: next }, `${label} ${idx + 1} spremljena!`);
    } catch (err) {
      toast.error(err.message || "Greška");
    }
  };

  const removeListItem = (field, idx, list, label) => {
    if (!confirm(`Ukloniti ${label} ${idx + 1}?`)) return;
    const next = list.filter((_, i) => i !== idx);
    save({ [field]: next }, `${label} uklonjena`);
  };

  const removeSingle = (field, label) => {
    if (!confirm(`Vratiti ${label} na default?`)) return;
    save({ [field]: "" }, `${label} resetiran`);
  };

  return (
    <Card className="p-6 rounded-2xl border-neutral-200" data-testid="images-manager">
      <div className="flex items-center gap-3 mb-1">
        <ImageIcon className="w-6 h-6 text-[#D32F2F]" />
        <h2
          className="text-2xl font-extrabold text-neutral-900"
          style={{ fontFamily: "Cabinet Grotesk" }}
        >
          Upravljanje slikama
        </h2>
      </div>
      <p className="text-sm text-neutral-500 mb-6">
        Klikni na sliku da je promijeniš. Promjene se vide odmah na stranici.
      </p>

      {/* LOGO */}
      <Section title="Logo (gore lijevo u navigaciji)" hint="PNG sa providnom pozadinom, preporučeno 200×200 px">
        <SinglePicker
          src={resolved.logo}
          label="logo"
          busy={busy}
          onPick={(e) => handleSingle(e, "logo", "Logo")}
          onRemove={resolved.logo ? () => removeSingle("logo", "Logo") : null}
          placeholder="Bez loga (prikazuje se 'M' bedž)"
          maxWidth="max-w-[160px]"
        />
      </Section>

      {/* HERO IMAGES */}
      <Section
        title="Hero slike (slideshow na vrhu)"
        hint="3 slike koje se izmjenjuju. Preporučeno 1920×1080 px."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <SinglePicker
              key={i}
              src={resolved.hero[i]}
              label={`Hero ${i + 1}`}
              busy={busy}
              onPick={(e) => handleList(e, "hero_images", i, resolved.hero, "Hero slika")}
              onRemove={
                resolved.hero[i]
                  ? () => removeListItem("hero_images", i, resolved.hero, "Hero slika")
                  : null
              }
              aspect="aspect-video"
            />
          ))}
        </div>
      </Section>

      {/* HISTORY GALLERY */}
      <Section
        title="Galerija povijesti (sekcija 'Od 2004')"
        hint="3 slike vaše lokacije."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <SinglePicker
              key={i}
              src={resolved.historyGallery[i]}
              label={`Galerija ${i + 1}`}
              busy={busy}
              onPick={(e) =>
                handleList(e, "history_images", i, resolved.historyGallery, "Galerija slika")
              }
              onRemove={
                resolved.historyGallery[i]
                  ? () =>
                      removeListItem(
                        "history_images",
                        i,
                        resolved.historyGallery,
                        "Galerija slika"
                      )
                  : null
              }
              aspect="aspect-square"
            />
          ))}
        </div>
      </Section>

      {/* BAG MASCOT */}
      <Section
        title="Maskota torbe (Knjižara sekcija)"
        hint="Najbolje slika torbe ili ilustracije, kvadratna."
      >
        <SinglePicker
          src={resolved.bagMascot}
          label="Maskota"
          busy={busy}
          onPick={(e) => handleSingle(e, "bag_image", "Maskota")}
          onRemove={resolved.bagMascot ? () => removeSingle("bag_image", "Maskota") : null}
          aspect="aspect-square"
          maxWidth="max-w-[200px]"
        />
      </Section>
    </Card>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="py-5 border-t border-neutral-100 first:border-t-0 first:pt-0">
      <div className="mb-3">
        <h3 className="font-bold text-neutral-900 text-base">{title}</h3>
        {hint && <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function SinglePicker({ src, label, busy, onPick, onRemove, aspect = "aspect-square", placeholder, maxWidth = "" }) {
  return (
    <div className={`relative group ${maxWidth}`}>
      <label
        className={`relative block ${aspect} rounded-xl overflow-hidden border-2 border-dashed border-neutral-300 hover:border-[#D32F2F] bg-neutral-50 cursor-pointer transition-colors`}
      >
        {src ? (
          <>
            <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors grid place-items-center opacity-0 group-hover:opacity-100">
              <span className="text-white text-sm font-medium bg-[#D32F2F] px-3 py-1.5 rounded-full">
                Promijeni
              </span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-center p-3">
            <div>
              <Upload className="w-6 h-6 mx-auto text-neutral-400 mb-1" />
              <span className="text-xs text-neutral-500 block">
                {placeholder || `Učitaj ${label}`}
              </span>
            </div>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={busy}
          data-testid={`upload-${label.toLowerCase().replace(/\s/g, "-")}`}
          onChange={onPick}
        />
      </label>
      {src && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 w-7 h-7 grid place-items-center bg-white hover:bg-red-50 rounded-full shadow-md border border-neutral-200 text-neutral-600 hover:text-[#D32F2F]"
          aria-label="Ukloni"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

